import dayjs from "dayjs";
import { useCallback, useEffect, useState } from "react";
import { useShallow } from "zustand/react/shallow";
import { trpc } from "~/api/trpc";
import { useUserStore } from "~/stores/user-store";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { isAuthError } from "~/lib/is-auth-error";

// Check if the error is a network error (connection issues, not auth)
const isNetworkError = (error: unknown): boolean => {
    if (!error) return false;
    // If it's a tRPC error with UNAUTHORIZED/FORBIDDEN, it's not a network error
    if (isAuthError(error)) return false;
    // All other errors are considered network errors
    return true;
};

const UserStoreHydrator = () => {
    const { user, setUser } = useUserStore(
        useShallow((state) => ({
            user: state.user,
            setUser: state.setUser,
        }))
    );

    const retry = useCallback(
        (failureCount: number, error: unknown) => !isAuthError(error) && failureCount < 3,
        [],
    );

    const userDataQuery = trpc.user.getMe.useQuery(undefined, {
        staleTime: 15 * 1000, // 15 seconds
        gcTime: 15 * 1000, // 15 seconds cache
        refetchInterval: 15 * 1000, // 15 seconds
        refetchOnReconnect: true,
        refetchIntervalInBackground: true,
        refetchOnWindowFocus: true,
        refetchOnMount: true,
        retry,
        retryDelay: 1500,
    });

    const trpcUtils = trpc.useUtils();

    const [isReconnectingToastId, setIsReconnectingToastId] = useState<string | number | null>(null);

    useEffect(() => {
        const next = userDataQuery.data;
        if (!next) return;

        const timezone = next.timezone.replace(/_/, "/");
        const practitionerId = next.practitionerId ?? null;
        const practitionerCategoryId = next.practitionerCategoryId ?? null;

        // Store user also carries isAuthenticated, which getMe never returns.
        // Comparing the whole objects would call setUser on every data reference change.
        if (
            user?.id === next.id &&
            user.email === next.email &&
            user.firstName === next.firstName &&
            user.lastName === next.lastName &&
            user.role === next.role &&
            user.timezone === timezone &&
            user.practitionerId === practitionerId &&
            user.practitionerCategoryId === practitionerCategoryId
        ) {
            return;
        }

        setUser({
            ...next,
            timezone,
            practitionerId,
            practitionerCategoryId,
        });
    }, [userDataQuery.data, user, setUser]);

    useEffect(() => {
        const failureCount = userDataQuery.failureCount;
        const hasNetworkError = isNetworkError(userDataQuery.failureReason);

        // Only show reconnecting toast for network errors, not auth errors
        if (failureCount >= 3 && isReconnectingToastId == null && hasNetworkError) {
            const id = toast.warning("Reconnecting to server...", { duration: 5 * 60 * 1000, icon: <Loader2 className='animate-spin' /> });
            setIsReconnectingToastId(id);
            return;
        }

        if (isReconnectingToastId != null && failureCount === 0) {
            // Successful reconnection
            toast.dismiss(isReconnectingToastId);
            toast.success("Connection re-established!");
            setIsReconnectingToastId(null);
            trpcUtils.invalidate(); // This refetches all current queries that may have been failing during the disconnect.
        }
    }, [userDataQuery.failureCount, isReconnectingToastId]);

    useEffect(() => {
        if (user?.timezone) {
            dayjs.tz.setDefault(user.timezone);
        }
    }, [user?.timezone]);

    return (
        <div className='hidden' />
    )
}

export default UserStoreHydrator
